import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Navigation from "../components/Navigation";
import Calendar from "../components/Calendar";
import { get, post } from "../utils/apiClient";
import { PregnancyDurationType } from "../types/allowance";
interface MaternityFormData {
  employeeId: string;
  cityCode: string;
  expectedDeliveryDate: string;
  birthType: "single" | "twins" | "triplets" | "quadruplets";
  hasExtendedDays: boolean; // 是否享受晚育/生育/奖励假
  rewardLeave: boolean;
  firstChild?: "yes" | "no";
  deliveryMethod: "normal" | "difficult" | "miscarriage";
  difficultType?: string[];
  pregnancyDuration?: number;
  pregnancyDurationType?: PregnancyDurationType;
  isMiscarriage?: boolean;
  employeeName?: string;
  lanId?: string;
  isDifficultBirth: boolean;
  numberOfBabies: number;
  maternityLeaveStartDate?: string; // API返回的实际产假开始日期
  maternityLeaveEndDate?: string; // API返回的实际产假结束日期
}

interface TimeScopeItem {
  index: number;
  name: string;
  days: number;
  startAt: string;
  endAt: string;
  details: Record<string, unknown>;
}

interface ApiCalculateResponse {
  lanId?: string;
  employeeName?: string;
  cityCode?: string;
  cityName?: string;
  totalDays?: number;
  total?: number;
  totalAllowanceDays?: number; // 享受津贴天数
  baseDays?: number; // 国家法定产假
  extendedDays?: number; // 地方奖励假
  dystociaDays?: number; // 难产假
  multiBabyDays?: number; // 多胞胎假
  startDate?: string;
  endDate?: string;
  returnToWorkDate?: string; // 预计返岗日期
  timeScopeList?: TimeScopeItem[]; // 时间范围列表
  isFirstTimeBirth?: boolean; // 是否为一孩（绍兴城市返回）
  // 兼容旧版本字段
  breakdown?: {
    baseDays?: number;
    additionalDays?: number;
    bonusDays?: number;
    difficultBirthDays?: number;
    multipleBirthDays?: number;
  };
  additionalDays?: number;
  bonusDays?: number;
  difficultBirthDays?: number;
  multipleBirthDays?: number;
  maternityPeriod?: { startDate?: string; endDate?: string };
  maternityStartDate?: string;
  maternityEndDate?: string;
  allowanceCompensationDetails?: string | string[] | Record<string, unknown>;
  details?: string | string[] | Record<string, unknown>;
  calculationDetail?: string | string[] | Record<string, unknown>;
}

interface HolidayItem {
  date: string; // yyyy-MM-dd
  name: string; // 原文名称
  name_cn: string; // 中文名
  name_en: string; // 英文名
  type: string; // 类型
}

// City 类型，遵循团队数据结构规范
interface City {
  id: string;
  name: string;
  code: string;
  chineseName: string;
  englishName: string;
  province: string;
  countryCode: string;
  enabled: boolean;
  sortOrder: number;
  remark?: Record<string, unknown> | null;
  createDate: string;
  createBy: string;
  updateDate: string;
  updateBy: string;
}

interface CalculationResult {
  totalDays: number;
  totalAllowanceDays?: number; // 享受津贴天数
  baseDays?: number; // 国家法定产假
  extendedDays?: number; // 地方奖励假
  dystociaDays?: number; // 难产假
  multiBabyDays?: number; // 多胞胎假
  breakdown: {
    baseDays: number;
    additionalDays: number;
    difficultBirthDays: number;
    multipleBirthDays: number;
  };
  maternityPeriod: {
    startDate: string;
    endDate: string;
  };
  returnToWorkDate?: string; // 预计返岗日期
  isFirstTimeBirth?: boolean; // 是否为一孩（绍兴城市返回）
  // 可选扩展字段，兼容后端
  bonusDays?: number;
  allowanceCompensationDetails?: string | string[] | Record<string, unknown>;
}

const MaternityCalculator: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState<MaternityFormData>({
    employeeId: "",
    cityCode: "SH",
    expectedDeliveryDate: "",
    birthType: "single",
    hasExtendedDays: true,
    rewardLeave: false,
    deliveryMethod: "normal",
    difficultType: [],
    lanId: "A5",
    employeeName: "",
    isDifficultBirth: false,
    numberOfBabies: 1,
    pregnancyDurationType: PregnancyDurationType.WEEKS,
    isMiscarriage: false,
  });

  const [result, setResult] = useState<CalculationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [holidays, setHolidays] = useState<HolidayItem[]>([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // 新增：流产规则
  const [miscarriageRules, setMiscarriageRules] = useState<Array<Record<string, unknown>>>([]);
  const [miscarriageRulesLoading, setMiscarriageRulesLoading] = useState(false);
  const [miscarriageRule, setMiscarriageRule] = useState<string>('');
  const [miscarriageFirstValue, setMiscarriageFirstValue] = useState<string>('');
  const [doctorRestDays, setDoctorRestDays] = useState<string>('');
  // API 返回的时间范围列表（用于产假日历核算高亮展示）
  const [timeScopes, setTimeScopes] = useState<TimeScopeItem[]>([]);
  const [highlightDates, setHighlightDates] = useState<Array<{ date: string; color: string; name: string }>>([]);
  // 产假日历核算的配色：从 CSS 变量中在运行时解析，避免在 JS 中硬编码颜色
  const CSS_RANGE_COLOR_VARS = [
    '--calendar-color-blue',
    '--calendar-color-green',
    '--calendar-color-purple',
    '--calendar-color-cyan-blue',
    '--calendar-color-deep-purple',
    '--calendar-color-teal-green',
    '--calendar-color-gold-brown',
    '--calendar-color-fuchsia',
  ] as const;

  const getCssVar = (name: string): string => {
    if (typeof window === 'undefined') return '';
    const styles = getComputedStyle(document.documentElement);
    // 优先读取标准的 --var 名称；若历史写成单 - 前缀，做一次兼容读取
    const v = styles.getPropertyValue(name).trim();
    if (v) return v;
    if (name.startsWith('--')) {
      const legacy = styles.getPropertyValue(name.replace(/^--/, '-')).trim();
      if (legacy) return legacy;
    }
    return '';
  };

  const [rangeColors, setRangeColors] = useState<string[]>([]);

  useEffect(() => {
    // 在首次渲染后，从 CSS 变量解析颜色
    const resolved = CSS_RANGE_COLOR_VARS.map((n) => getCssVar(n));
    setRangeColors(resolved);
  }, []);
  const cityOptions = cities.map((city) => ({
    value: city.code,
    label: city.chineseName || city.name,
  }));

  // 获取假期数据
  const fetchHolidays = async () => {
    setHolidaysLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      const years = [currentYear - 2, currentYear - 1, currentYear]; // 去年、今年

      const holidayPromises = years.map((year) =>
        get<HolidayItem[]>(`https://www.54cpa.cn/api/support/holidays/${year}`),
      );

      const holidayResults = await Promise.all(holidayPromises);
      const allHolidays = holidayResults.flat();

      setHolidays(allHolidays);
    } catch (error) {
      console.error("获取假期数据失败:", error);
    } finally {
      setHolidaysLoading(false);
    }
  };

  // 获取城市数据
  const fetchCities = async () => {
    setCitiesLoading(true);
    try {
      // 接口返回 { code, message, data }，apiClient 已抽取 data
      const data = await get<City[]>("https://www.54cpa.cn/api/support/cities");
      // 按 sortOrder 升序（不按 enabled 过滤，避免后端标识差异导致全部被过滤）
      const sorted = (data || [])
        .slice()
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setCities(sorted);

      // 若当前 cityCode 不在返回列表中，则设置为第一个可用城市
      if (
        sorted.length > 0 &&
        !sorted.some((c) => c.code === formData.cityCode)
      ) {
        setFormData((prev) => ({ ...prev, cityCode: sorted[0].code }));
      }
    } catch (error) {
      console.error("获取城市数据失败:", error);
    } finally {
      setCitiesLoading(false);
    }
  };

  // 组件加载时获取假期数据
  useEffect(() => {
    // 避免重复调用，只在 holidays 为空时调用
    if (holidays.length === 0 && !holidaysLoading) {
      fetchHolidays();
    }
    // 我们希望仅在初始化时拉取假期数据
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 组件加载时获取城市数据
  useEffect(() => {
    if (cities.length === 0 && !citiesLoading) {
      fetchCities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 当选择“流产”且存在城市代码时，获取对应的流产规则
  useEffect(() => {
    const isMis = formData.deliveryMethod === 'miscarriage';
    if (!isMis || !formData.cityCode) {
      setMiscarriageRules([]);
      setMiscarriageRule('');
      setMiscarriageFirstValue('');
      setDoctorRestDays('');
      return;
    }
    const fetchRules = async () => {
      setMiscarriageRulesLoading(true);
      try {
        const url = `https://www.54cpa.cn/api/maternity-leave/ref-data/miscarriage-rules?cityCode=${encodeURIComponent(formData.cityCode)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const arr = Array.isArray(json)
          ? json
          : (Array.isArray((json && (json.data as unknown)))
              ? (json.data as unknown as Array<Record<string, unknown>>)
              : []);
        setMiscarriageRules(arr as Array<Record<string, unknown>>);
        if (arr && arr.length > 0) {
          const first = arr[0] as Record<string, unknown>;
          const val = String(first["code"] ?? first["id"] ?? first["value"] ?? first["description"] ?? "");
          setMiscarriageFirstValue(val);
          setMiscarriageRule(val);
        } else {
          setMiscarriageRule('');
          setMiscarriageFirstValue('');
        }
      } catch (e) {
        console.error('获取流产规则失败:', e);
        setMiscarriageRules([]);
        setMiscarriageRule('');
        setMiscarriageFirstValue('');
      } finally {
        setMiscarriageRulesLoading(false);
      }
    };
    fetchRules();
  }, [formData.deliveryMethod, formData.cityCode]);

  // 处理从历史记录传入的数据
  useEffect(() => {
    const state = location.state as {
      formData?: Partial<MaternityFormData>;
      fromHistory?: boolean;
    } | null;
    if (state?.fromHistory && state.formData) {
      console.log("📥 从历史记录接收到的数据:", state.formData);
      console.log("🔍 firstChild值:", state.formData?.firstChild);
      setFormData((prev) => ({
        ...prev,
        employeeName: state.formData?.employeeName || prev.employeeName,
        lanId: state.formData?.lanId || prev.lanId,
        employeeId: state.formData?.employeeId || prev.employeeId,
        cityCode: state.formData?.cityCode || prev.cityCode,
        expectedDeliveryDate:
          state.formData?.expectedDeliveryDate || prev.expectedDeliveryDate,
        birthType: state.formData?.birthType || prev.birthType,
        hasExtendedDays:
          state.formData?.hasExtendedDays ?? prev.hasExtendedDays,
        rewardLeave: state.formData?.rewardLeave ?? prev.rewardLeave,
        deliveryMethod: state.formData?.deliveryMethod || prev.deliveryMethod,
        pregnancyDuration:
          state.formData?.pregnancyDuration || prev.pregnancyDuration,
        firstChild: state.formData?.firstChild || prev.firstChild, // 绍兴一孩选项
      }));

      // 同时设置日历选中的日期
      if (state.formData?.expectedDeliveryDate) {
        setSelectedDate(new Date(state.formData.expectedDeliveryDate));
      }
    }
  }, [location.state]);

  // 将 HolidayItem 转换为 Calendar 组件所需的 HolidayInfo 格式
  const convertToHolidayInfo = (holidays: HolidayItem[]) => {
    return holidays.map((holiday) => ({
      date: holiday.date,
      name: holiday.name_cn,
      type:
        holiday.type === "public_holiday"
          ? ("holiday" as const)
          : ("workday" as const),
    }));
  };

  // 工具函数保留：获取假期信息（供日历渲染使用）

  // 员工工号验证函数
  const validateEmployeeId = (employeeId: string) => {
    const employeeIdPattern = /^[Aa]\d{7}$/;

    if (employeeId && !employeeIdPattern.test(employeeId)) {
      setValidationErrors((prev) => ({
        ...prev,
        lanId: "员工工号格式错误，应为A5开头加6位数字（如：A5123456）",
      }));
      return false;
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.lanId;
        return newErrors;
      });
      return true;
    }
  };

  const handleInputChange = <K extends keyof MaternityFormData>(
    field: K,
    value: MaternityFormData[K],
  ) => {
    const criticalFields = [
      "cityCode",
      "expectedDeliveryDate",
      "deliveryMethod",
      "birthType",
      "hasExtendedDays",
      "difficultType",
      "pregnancyDuration",
      "isDifficultBirth",
      "numberOfBabies",
    ];

    // 如果修改的是关键参数且有计算结果，则清除结果与区间
    if (criticalFields.includes(field as string) && result !== null) {
      setResult(null);
      setTimeScopes([]);
    }

    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      // 绍兴城市特殊处理：如果选择绍兴且firstChild未设置，默认为"yes"
      if (field === "cityCode" && value === "SX" && !newData.firstChild) {
        newData.firstChild = "yes";
      }
      return newData;
    });

    // 清除该字段的验证错误
    if (validationErrors[field as string]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field as string];
        return newErrors;
      });
    }

    // 员工工号实时验证
    if (field === "lanId") {
      validateEmployeeId(value as string);
    }
  };

  const handleDifficultTypeChange = (type: string, checked: boolean) => {
    // 清除计算结果，因为难产类型是关键参数
    if (result !== null) {
      setResult(null);
    }

    setFormData((prev) => ({
      ...prev,
      difficultType: checked
        ? [...(prev.difficultType || []), type]
        : (prev.difficultType || []).filter((t) => t !== type),
    }));
  };

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date);

    // 清除计算结果与区间，因为预产期是关键参数
    if (result !== null) {
      setResult(null);
      setTimeScopes([]);
    }

    if (date) {
      // Fix timezone issue by using local date string format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const localDateString = `${year}-${month}-${day}`;
      setFormData((prev) => ({
        ...prev,
        expectedDeliveryDate: localDateString,
      }));
    } else {
      // 清空日期时，同时清空 expectedDeliveryDate
      setFormData((prev) => ({ ...prev, expectedDeliveryDate: "" }));
    }
  };

  const calculateMaternity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.expectedDeliveryDate) return;
    setLoading(true);
    try {
      setErrorMessage("");

      // 构造符合后端要求的请求体
      const isMiscarriage = formData.deliveryMethod === "miscarriage";

      const computeMiscarriageDetail = () => {
        if (!isMiscarriage) return undefined;
        // 计算选中项的 index（从1开始）
        const valueOf = (item: Record<string, unknown>, idx: number) => {
          return String(
            item["code"] ?? item["id"] ?? item["value"] ?? item["description"] ?? `rule_${idx}`
          );
        };
        let index = 1;
        for (let i = 0; i < miscarriageRules.length; i++) {
          const v = valueOf(miscarriageRules[i] as Record<string, unknown>, i);
          if (v === miscarriageRule) {
            index = i + 1;
            break;
          }
        }
        // 计算 days
        let days = 0;
        if (
          formData.cityCode === "SZ" &&
          miscarriageRule === miscarriageFirstValue
        ) {
          const n = parseInt(doctorRestDays || "0", 10);
          days = Number.isFinite(n) && n >= 0 ? n : 0;
        } else {
          const selIndex = Math.max(0, index - 1);
          const item = miscarriageRules[selIndex] as
            | Record<string, unknown>
            | undefined;
          const rawDays = item && (item["days"] ?? item["day"] ?? item["valueDays"]);
          if (typeof rawDays === "number") {
            days = rawDays;
          } else if (typeof rawDays === "string") {
            const n = parseInt(rawDays, 10);
            days = Number.isFinite(n) ? n : 0;
          } else {
            const desc = item && (item["description"] ?? item["name"]);
            if (typeof desc === "string") {
              const m = desc.match(/(\d{1,3})/);
              if (m) {
                const n = parseInt(m[1], 10);
                days = Number.isFinite(n) ? n : 0;
              }
            }
          }
        }
        return {
          cityCode: formData.cityCode,
          index,
          days,
        };
      };

      const payload = {
        lanId: formData.lanId || "",
        employeeName: formData.employeeName || "",
        cityCode: formData.cityCode,
        isMiscarriage,
        isDifficultBirth: !!formData.isDifficultBirth,
        numberOfBabies: Number(formData.numberOfBabies || 1),
        expectedDeliveryDate: formData.expectedDeliveryDate,
        hasExtendedDays: formData.hasExtendedDays,
        ...(isMiscarriage
          ? { miscarriageLeaveDetail: computeMiscarriageDetail() }
          : {}),
        // 绍兴城市特殊参数：是否为一孩
        ...(formData.cityCode === "SX" && formData.firstChild ? { isFirstTimeBirth: formData.firstChild === "yes" } : {}),
      };

      // 调用真实接口，apiClient 将自动抽取 data 字段
      const apiData = await post<ApiCalculateResponse>(
        "https://www.54cpa.cn/api/maternity-leave/calculate",
        payload,
      );

      // 兼容后端返回结构，做一次归一化，避免渲染期字段缺失导致崩溃
      const normalized: CalculationResult = {
        totalDays: apiData?.totalDays ?? apiData?.total ?? 0,
        totalAllowanceDays: apiData?.totalAllowanceDays ?? 0, // 享受津贴天数
        baseDays: apiData?.baseDays ?? apiData?.breakdown?.baseDays ?? 0,
        extendedDays:
          apiData?.extendedDays ??
          apiData?.bonusDays ??
          apiData?.breakdown?.additionalDays ??
          0,
        dystociaDays:
          apiData?.dystociaDays ?? apiData?.breakdown?.difficultBirthDays ?? 0,
        multiBabyDays:
          apiData?.multiBabyDays ?? apiData?.breakdown?.multipleBirthDays ?? 0,
        breakdown: {
          baseDays: apiData?.breakdown?.baseDays ?? apiData?.baseDays ?? 0,
          additionalDays:
            apiData?.breakdown?.additionalDays ??
            apiData?.additionalDays ??
            apiData?.breakdown?.bonusDays ??
            apiData?.bonusDays ??
            0,
          difficultBirthDays:
            apiData?.breakdown?.difficultBirthDays ??
            apiData?.difficultBirthDays ??
            0,
          multipleBirthDays:
            apiData?.breakdown?.multipleBirthDays ??
            apiData?.multipleBirthDays ??
            0,
        },
        maternityPeriod: {
          startDate:
            apiData?.maternityPeriod?.startDate ??
            apiData?.startDate ??
            apiData?.maternityStartDate ??
            formData.expectedDeliveryDate,
          endDate:
            apiData?.maternityPeriod?.endDate ??
            apiData?.endDate ??
            apiData?.maternityEndDate ??
            formData.expectedDeliveryDate,
        },
        bonusDays:
          apiData?.breakdown?.bonusDays ??
          apiData?.bonusDays ??
          apiData?.breakdown?.additionalDays ??
          apiData?.additionalDays,
        allowanceCompensationDetails:
          apiData?.allowanceCompensationDetails ??
          apiData?.details ??
          apiData?.calculationDetail,
        returnToWorkDate: apiData?.returnToWorkDate, // 使用接口返回的预计返岗日期
        isFirstTimeBirth: apiData?.isFirstTimeBirth, // 绍兴城市返回的一孩结果
      };

      setResult(normalized);

      // 保存时间范围列表（若存在）
      const timeScopeList = Array.isArray(apiData?.timeScopeList) ? apiData.timeScopeList : [];
      setTimeScopes(timeScopeList);

      // 提取所有 details 中的日期，生成 highlightDates 数组
      const extractedHighlightDates: Array<{ date: string; color: string; name: string }> = [];
      timeScopeList.forEach((item) => {
        if (item.details && typeof item.details === 'object') {
          Object.keys(item.details).forEach((dateStr) => {
            if (typeof dateStr === 'string' && /\d{4}-\d{2}-\d{2}/.test(dateStr)) {
              const holidayName = typeof item.details[dateStr] === 'string' ? item.details[dateStr] as string : '假期顺延';
              extractedHighlightDates.push({
                date: dateStr,
                color: getCssVar('--calendar-color-gold-brown'), // 使用CSS变量作为假期顺延颜色
                name: holidayName
              });
            }
          });
        }
      });
      setHighlightDates(extractedHighlightDates);

      // 更新 formData 以保存 API 返回的日期数据，供津贴计算使用
      setFormData((prev) => ({
        ...prev,
        maternityLeaveStartDate: normalized.maternityPeriod.startDate,
        maternityLeaveEndDate: normalized.maternityPeriod.endDate,
      }));

      // 保存到历史记录
      saveToHistory(normalized, formData);
    } catch (error: unknown) {
      console.error("计算失败:", error);
      const message =
        typeof error === "object" && error && "message" in error
          ? String((error as { message?: unknown }).message)
          : "计算失败，请稍后重试";
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  // 保存计算结果到历史记录
  const saveToHistory = (
    result: CalculationResult,
    formData: MaternityFormData,
  ) => {
    try {
      // 生成唯一ID
      const recordId = `maternity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // 获取城市名称
      const selectedCity = cities.find(
        (city) => city.code === formData.cityCode,
      );
      const cityName =
        selectedCity?.chineseName || selectedCity?.name || formData.cityCode;

      // 构造历史记录对象
      const historyRecord = {
        id: recordId,
        employeeName: formData.employeeName || "",
        employeeId: formData.lanId || formData.employeeId || "",
        city: formData.cityCode,
        cityName: cityName,
        startDate: result.maternityPeriod.startDate,
        endDate: result.maternityPeriod.endDate,
        totalDays: result.totalDays,
        companyCompensation: 0, // 产假计算阶段暂时为0，津贴计算后会更新
        calculatedAt: new Date().toISOString(),
        recordType: "maternity",
        vacationData: {
          birthType: formData.birthType,
          hasExtendedDays: formData.hasExtendedDays,
          rewardLeave: formData.rewardLeave,
          deliveryMethod: formData.deliveryMethod,
          pregnancyDuration: formData.pregnancyDuration,
          firstChild: formData.firstChild, // 绍兴一孩选项（用户输入）
          isFirstTimeBirth: result.isFirstTimeBirth, // 绍兴一孩结果（后端返回）
        },
        allowanceData: {},
      };

      // 获取现有历史记录
      const existingRecords = JSON.parse(
        localStorage.getItem("hr_maternity_history") || "[]",
      );

      // 检查是否已存在相同员工的记录，如果存在则替换（每个工号只保留最新记录）
      const filteredRecords = existingRecords.filter(
        (record: { employeeId: string }) =>
          record.employeeId !== historyRecord.employeeId,
      );

      // 添加新记录
      const updatedRecords = [historyRecord, ...filteredRecords];

      // 保存到localStorage
      localStorage.setItem(
        "hr_maternity_history",
        JSON.stringify(updatedRecords),
      );

      console.log("✅ 产假计算结果已保存到历史记录:", historyRecord);
    } catch (error) {
      console.error("❌ 保存历史记录失败:", error);
    }
  };

  const navigateToAllowance = () => {
    if (result) {
      // 传递计算结果到津贴计算页面
      navigate("/allowance", { state: { maternityResult: result, formData } });
    }
  };

  // 点击外部关闭导出菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showExportMenu && !target.closest(".export-container")) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showExportMenu]);

  const showGuangzhouOptions =
    formData.cityCode === "GZ" && formData.deliveryMethod === "difficult";
  const showShaoxingFirstChild = formData.cityCode === "SX";

  return (
    <div>
      <Header />
      <Navigation />

      <div className="container">
        <header className="header">
          <div className="header-content">
            <i className="fas fa-baby header-icon" />
            <h1>全国产假计算器</h1>
            <p className="subtitle">
              支持全国各省会城市，为女性员工提供准确的产假时间计算
            </p>
          </div>
        </header>

        <main className="main-content">
          <div className="two-column-layout">
            <div className="calculator-section">
              <div className="calculator-card">
              <div className="card-header">
                <h2>
                  <i className="fas fa-calendar-alt" /> 产假计算
                </h2>
              </div>

              

              <form className="calculator-form" onSubmit={calculateMaternity}>
                {/* 城市选择 */}
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-map-marker-alt" />
                    选择城市
                  </label>
                  <select
                    className="form-input"
                    value={formData.cityCode}
                    onChange={(e) =>
                      handleInputChange("cityCode", e.target.value)
                    }
                    disabled={citiesLoading || cityOptions.length === 0}
                    required
                  >
                    {cityOptions.length === 0 ? (
                      <option value="" disabled>
                        加载城市中...
                      </option>
                    ) : (
                      cityOptions.map((city) => (
                        <option key={city.value} value={city.value}>
                          {city.label}
                        </option>
                      ))
                    )}
                  </select>
                  <small className="form-help">
                    不同城市的产假政策可能有所差异
                  </small>
                </div>
                {/* 姓名 */}
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-user" />
                    员工姓名
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="请输入员工姓名"
                    value={formData.employeeName || ""}
                    onChange={(e) =>
                      handleInputChange("employeeName", e.target.value)
                    }
                    required
                  />
                </div>

                {/* 工号 */}
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-id-card" />
                    员工工号
                  </label>
                  <input
                    type="text"
                    className={`form-input ${validationErrors.lanId ? "error" : ""}`}
                    placeholder="请输入员工工号（如：A5123456）"
                    value={formData.lanId || ""}
                    pattern="[Aa]\d{7}"
                    maxLength={8}
                    onChange={(e) => handleInputChange("lanId", e.target.value)}
                    required
                  />
                  {validationErrors.lanId ? (
                    <small className="form-error">
                      {validationErrors.lanId}
                    </small>
                  ) : (
                    <small className="form-help">
                      格式：A5开头加6位数字（如：A5123456）
                    </small>
                  )}
                </div>

                {/* 日期选择 */}
                <Calendar
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  label="产假开始日期"
                  helpText="请选择产假开始日期"
                  holidays={convertToHolidayInfo(holidays)}
                  defaultCollapsed={false}
                />

                {/* 生育方式 - 改为单选 */}
                <div className="form-group">
                  <label className="form-label">
                    <i className="fas fa-medical" />
                    生育方式
                  </label>
                  <div className="delivery-methods">
                    <div
                      className={`delivery-section ${formData.deliveryMethod === "normal" ? "delivery-normal" : ""}`}
                    >
                      <label className="radio-label delivery-main">
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="normal"
                          checked={formData.deliveryMethod === "normal"}
                          onChange={() => {
                            // 清除计算结果，因为生育方式是关键参数
                            if (result !== null) {
                              setResult(null);
                            }

                            setFormData((prev) => ({
                              ...prev,
                              deliveryMethod: "normal",
                              isDifficultBirth: false,
                            }));
                          }}
                        />
                        <span className="radio-mark" />
                        顺产
                      </label>
                    </div>

                    <div
                      className={`delivery-section difficult-delivery ${formData.deliveryMethod === "difficult" ? "delivery-difficult" : ""}`}
                    >
                      <label className="radio-label delivery-main">
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="difficult"
                          checked={formData.deliveryMethod === "difficult"}
                          onChange={() => {
                            // 清除计算结果，因为生育方式是关键参数
                            if (result !== null) {
                              setResult(null);
                            }

                            setFormData((prev) => ({
                              ...prev,
                              deliveryMethod: "difficult",
                              isDifficultBirth: true,
                            }));
                          }}
                        />
                        <span className="radio-mark" />
                        难产
                      </label>

                      {/* 广州难产特殊选项 */}
                      {formData.deliveryMethod === "difficult" &&
                        showGuangzhouOptions && (
                          <div className="delivery-sub-options">
                            <small className="form-help">广州市可选择：</small>
                            <label className="checkbox-label sub-option">
                              <input
                                type="checkbox"
                                checked={(
                                  formData.difficultType || []
                                ).includes("cesarean")}
                                onChange={(e) =>
                                  handleDifficultTypeChange(
                                    "cesarean",
                                    e.target.checked,
                                  )
                                }
                              />
                              <span className="checkmark" />
                              剖腹产、会阴III度破裂
                            </label>
                            <label className="checkbox-label sub-option">
                              <input
                                type="checkbox"
                                checked={(
                                  formData.difficultType || []
                                ).includes("assisted")}
                                onChange={(e) =>
                                  handleDifficultTypeChange(
                                    "assisted",
                                    e.target.checked,
                                  )
                                }
                              />
                              <span className="checkmark" />
                              吸引产、钳产、臀位牵引产
                            </label>
                          </div>
                        )}
                    </div>

                    <div
                      className={`delivery-section ${formData.deliveryMethod === "miscarriage" ? "delivery-miscarriage" : ""}`}
                    >
                      <label className="radio-label delivery-main">
                        <input
                          type="radio"
                          name="deliveryMethod"
                          value="miscarriage"
                          checked={formData.deliveryMethod === "miscarriage"}
                          onChange={() => {
                            // 清除计算结果，因为生育方式是关键参数
                            if (result !== null) {
                              setResult(null);
                            }

                            setFormData((prev) => ({
                              ...prev,
                              deliveryMethod: "miscarriage",
                              isDifficultBirth: false,
                            }));
                          }}
                        />
                        <span className="radio-mark" />
                        流产
                      </label>

                      {formData.deliveryMethod === "miscarriage" && (
                        <div className="delivery-sub-options">
                          <label className="form-label sub-label">
                            <i className="fas fa-info-circle" /> 流产规则
                          </label>
                          {/* 未选择城市时的提示 */}
                          {!formData.cityCode && (
                            <div className="warning-prompt" style={{
                              padding: '12px',
                              backgroundColor: 'var(--warning-bg)',
                              border: '1px solid var(--warning-border)',
                              borderRadius: '6px',
                              color: 'var(--warning-text)',
                              fontSize: '14px',
                              marginBottom: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <i className="fas fa-exclamation-triangle" style={{ color: 'var(--warning-color)' }}></i>
                              请先选择城市
                            </div>
                          )}
                          <div className="input-with-button">
                            <select
                              className="form-input"
                              value={miscarriageRule}
                              onChange={(e) => setMiscarriageRule(e.target.value)}
                              disabled={miscarriageRulesLoading}
                            >
                              {miscarriageRulesLoading && <option>加载中...</option>}
                              {!miscarriageRulesLoading && miscarriageRules.length === 0 && (
                                <option value="">无可用规则</option>
                              )}
                              {!miscarriageRulesLoading && miscarriageRules.length > 0 && (
                                miscarriageRules.map((item, idx) => {
                                  const val = (item['code'] ?? item['id'] ?? item['value'] ?? item['description'] ?? `rule_${idx}`).toString();
                                  const label = (item['description'] ?? item['name'] ?? val).toString();
                                  return (
                                    <option key={val} value={val}>{label}</option>
                                  );
                                })
                              )}
                            </select>
                          </div>
                          {formData.cityCode === 'SZ' && miscarriageRule === miscarriageFirstValue && (
                            <div className="form-group" style={{ marginTop: 10 }}>
                              <label className="form-label sub-label">
                                <i className="fas fa-user-md" /> 医生建议休息天数
                              </label>
                              <input
                                type="number"
                                className="form-input w-full"
                                min="0"
                                placeholder="请输入医生建议的休息天数"
                                value={doctorRestDays}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  // 仅允许非负整数
                                  if (/^\d*$/.test(v)) setDoctorRestDays(v);
                                }}
                              />
                            </div>
                          )}
                          <small className="form-help"></small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 胎儿数量 - 流产时隐藏 */}
                {formData.deliveryMethod !== "miscarriage" && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-baby" />
                      几胞胎
                    </label>
                    <select
                      className="form-input"
                      value={formData.birthType}
                      onChange={(e) => {
                        const value = e.target
                          .value as MaternityFormData["birthType"];
                        const map: Record<
                          MaternityFormData["birthType"],
                          number
                        > = {
                          single: 1,
                          twins: 2,
                          triplets: 3,
                          quadruplets: 4,
                        };
                        // 清除计算结果，因为胎数类型是关键参数
                        if (result !== null) {
                          setResult(null);
                        }

                        setFormData((prev) => ({
                          ...prev,
                          birthType: value,
                          numberOfBabies: map[value],
                        }));
                      }}
                      required
                    >
                      <option value="single">单胎</option>
                      <option value="twins">双胞胎</option>
                      <option value="triplets">三胞胎</option>
                      <option value="quadruplets">四胞胎</option>
                    </select>
                    <small className="form-help">选择胎儿数量类型</small>
                  </div>
                )}

                {/* 晚育/奖励假 - 流产时隐藏 */}
                {formData.deliveryMethod !== "miscarriage" && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-calendar-plus" />
                      晚育/生育/奖励假
                    </label>
                    <div className="checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.hasExtendedDays}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              hasExtendedDays: e.target.checked,
                            })
                          }
                        />
                        <span className="checkmark" />
                        晚育/生育/奖励假
                      </label>
                    </div>
                  </div>
                )}

                {/* 绍兴特殊选项 */}
                {showShaoxingFirstChild && (
                  <div className="form-group">
                    <label className="form-label">
                      <i className="fas fa-baby" />
                      是否生育一孩
                    </label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="firstChild"
                          value="yes"
                          checked={formData.firstChild === "yes"}
                          onChange={() =>
                            handleInputChange("firstChild", "yes")
                          }
                        />
                        <span className="radio-mark" />是
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="firstChild"
                          value="no"
                          checked={formData.firstChild === "no"}
                          onChange={() => handleInputChange("firstChild", "no")}
                        />
                        <span className="radio-mark" />否
                      </label>
                    </div>
                    <small className="form-help">绍兴市政策要求</small>
                  </div>
                )}

                <div className="button-group">
                  <button
                    type="submit"
                    className="calculate-btn"
                    disabled={loading || !formData.expectedDeliveryDate}
                  >
                    <i className="fas fa-calculator" />
                    {loading ? "计算中..." : "计算假期时间"}
                  </button>

                  <button
                    type="button"
                    className="calculate-btn secondary-btn"
                    disabled={!result}
                    onClick={navigateToAllowance}
                  >
                    <i className="fas fa-money-bill-wave" />
                    计算津贴
                  </button>
                </div>

                {errorMessage && (
                  <div
                    className="error-message"
                    role="alert"
                    style={{ color: "var(--error-color)", marginTop: "8px" }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ marginRight: "6px" }}
                    />
                    "请求失败，请稍后再试"
                  </div>
                )}
              </form>

              {/* 计算结果 */}
              {result && (
                <div className="result-section">
                  <div className="result-header">
                    <h3
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span>
                        <i className="fas fa-check-circle" /> 计算结果
                      </span>
                      <div
                        className="export-container"
                        style={{ position: "relative" }}
                      >
                      </div>
                    </h3>
                  </div>

                  <div className="result-grid">
                    <div className="result-item">
                      <div className="result-label">产假开始日期</div>
                      <div className="result-value">
                        {formData.expectedDeliveryDate
                          ? new Date(
                              formData.expectedDeliveryDate,
                            ).toLocaleDateString("zh-CN")
                          : "-"}
                      </div>
                    </div>
                    <div className="result-item">
                      <div className="result-label">产假结束日期</div>
                      <div className="result-value highlight">
                        {result.maternityPeriod.endDate
                          ? new Date(
                              result.maternityPeriod.endDate,
                            ).toLocaleDateString("zh-CN")
                          : "-"}
                      </div>
                    </div>
                    <div className="result-item">
                      <div className="result-label">总产假天数</div>
                      <div className="result-value">{result.totalDays}天</div>
                    </div>
                    <div className="result-item">
                      <div className="result-label">预计返岗日期</div>
                      <div className="result-value highlight">
                        {result.returnToWorkDate
                          ? new Date(
                              result.returnToWorkDate,
                            ).toLocaleDateString("zh-CN")
                          : "-"}
                      </div>
                    </div>
                  </div>

                  {/* 产假构成明细 - 流产时隐藏 */}
                  {formData.deliveryMethod !== "miscarriage" && (
                    <div className="breakdown-section">
                      <h4>
                        <i className="fas fa-list-ul" /> 产假构成明细
                      </h4>
                      <div className="breakdown-list">
                      {/* 国家法定产假 - 当baseDays > 0时显示 */}
                      {(result.baseDays ?? result.breakdown?.baseDays ?? 0) >
                        0 && (
                        <div className="breakdown-item">
                          <span>国家法定产假：</span>
                          <span>
                            {result.baseDays ?? result.breakdown?.baseDays ?? 0}
                            天
                          </span>
                        </div>
                      )}

                      {/* 地方奖励假 - 当extendedDays > 0时显示 */}
                      {(result.extendedDays ??
                        result.bonusDays ??
                        result.breakdown?.additionalDays ??
                        0) > 0 && (
                        <div className="breakdown-item">
                          <span>地方奖励假：</span>
                          <span>
                            {result.extendedDays ??
                              result.bonusDays ??
                              result.breakdown?.additionalDays ??
                              0}
                            天
                          </span>
                        </div>
                      )}

                      {/* 其他顺延假 - 智能显示逻辑 */}
                      {(() => {
                        const dystociaDays =
                          result.dystociaDays ??
                          result.breakdown?.difficultBirthDays ??
                          0;
                        const multiBabyDays =
                          result.multiBabyDays ??
                          result.breakdown?.multipleBirthDays ??
                          0;

                        if (dystociaDays > 0 && multiBabyDays > 0) {
                          // 两项都大于0，显示"其他顺延假"
                          const tooltipContent = `难产假：${dystociaDays}天\n多胞胎假：${multiBabyDays}天`;
                          return (
                            <div
                              className="breakdown-item"
                              title={tooltipContent}
                              style={{ cursor: "help" }}
                            >
                              <span>其他顺延假：</span>
                              <span>{dystociaDays + multiBabyDays}天</span>
                            </div>
                          );
                        } else if (dystociaDays > 0) {
                          // 只有难产假
                          const tooltipContent = `难产假详情：\n因难产情况额外增加的产假天数`;
                          return (
                            <div
                              className="breakdown-item"
                              title={tooltipContent}
                              style={{ cursor: "help" }}
                            >
                              <span>其他顺延假：</span>
                              <span>{dystociaDays}天</span>
                            </div>
                          );
                        } else if (multiBabyDays > 0) {
                          // 只有多胞胎假
                          const tooltipContent = `多胞胎假详情：\n因多胞胎生育额外增加的产假天数`;
                          return (
                            <div
                              className="breakdown-item"
                              title={tooltipContent}
                              style={{ cursor: "help" }}
                            >
                              <span>其他顺延假：</span>
                              <span>{multiBabyDays}天</span>
                            </div>
                          );
                        }
                        // 都为0时不显示
                        return null;
                      })()}
                      </div>
                    </div>
                  )}
                  {timeScopes && timeScopes.length > 0 && (
                    <div className="breakdown-section" style={{ marginTop: 24 }}>
                      <h4>
                        <i className="fas fa-calendar-alt" /> 产假日历核算
                      </h4>
                      <div>
                        <Calendar
                          selectedDate={null}
                          onDateSelect={() => { /* 展示用途，不改变表单状态 */ }}
                          label=""
                          helpText=""
                          holidays={convertToHolidayInfo(holidays)}
                          defaultCollapsed={false}
                          initialMonthDate={timeScopes[0]?.startAt}
                          ranges={timeScopes.map((t, i) => ({
                            start: t.startAt,
                            end: t.endAt,
                            name: t.name,
                            // 若能解析到 CSS 变量颜色则传入；否则留空让 Calendar 内部调色板接管
                            ...(rangeColors[i % rangeColors.length]
                              ? { color: rangeColors[i % rangeColors.length] }
                              : {}),
                          }))}
                          highlightDates={highlightDates}
                          showDefaultLegend={false}
                          collapsible={false}
                          showHeader={false}
                        />
                        {/* 颜色图例提示（与 ranges 保持一致） */}
                        <div className="calendar-legend" style={{ marginTop: 8 }}>
                          {timeScopes.map((t, i) => {
                            const color = rangeColors[i % (rangeColors.length || 1)] || undefined;
                            return (
                              <div className="legend-item" key={`${t.name}-${i}`}>
                                <div className="legend-color" style={color ? { background: color } : undefined}></div>
                                <span>{t.name}</span>
                              </div>
                            );
                          })}
                          {highlightDates.length > 0 && (
                            <div className="legend-item">
                              <div className="legend-color" style={{ background: getCssVar('--calendar-color-gold-brown')}}></div>
                              <span>假期顺延</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>

            <div className="info-section">
              {/* 政策说明 */}
              <div className="info-card">
                <div className="card-header">
                  <h3>
                    <i className="fas fa-info-circle" /> 产假政策说明
                  </h3>
                </div>
                <div className="info-content">
                  <ul className="policy-list">
                    <li>
                      <strong>国家法定产假：</strong>98天
                    </li>
                    <li>
                      <strong>地方奖励假：</strong>根据各地政策不同，30-90天不等
                    </li>
                    <li>
                      <strong>难产情况：</strong>额外增加15-30天
                    </li>
                    <li>
                      <strong>多胞胎：</strong>每多生育1个婴儿，增加15天
                    </li>
                    <li>
                      <strong>薪资待遇：</strong>产假期间享受生育津贴
                    </li>
                    <li>
                      <strong>节假日顺延：</strong>遇周末和法定节假日自动顺延
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="footer">
          <p> 2025 Twins HR产假津贴管理系统 | 仅供内部使用</p>
        </footer>
      </div>
    </div>
  );
};

export default MaternityCalculator;
